import { useIssuePersonalCoupon } from '@/features/promotions/hooks/usePromotions';
import { useCustomerSearch } from '@/features/customers/hooks/useCustomers';
import { useErrorT, useT } from '@/i18n';
import { resolveErrorText } from '@/api/errors';
import type { Customer } from '@restaurant/api-client';
import {
  BlockTitle,
  Block,
  List,
  ListInput,
  ListItem,
  Link as F7Link,
  Button,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useState } from 'react';

function customerName(c: Customer, fallback: string): string {
  return (
    `${c.firstName} ${c.lastName}`.trim() ||
    c.phoneNumbers.find((p) => p.isPrimary)?.phone ||
    fallback
  );
}

/**
 * Mint a PRIVATE coupon for a chosen customer against this promotion. Shown only
 * for a saved PRIVATE promotion. The issued/redeemed history and the customer's
 * own coupon wallet are part of the customer-accounts surface.
 */
export function IssueCouponSection({ promotionId }: { promotionId: string }) {
  const t = useT();
  const tp = t.promotions;
  const errorMessages = useErrorT();
  const issue = useIssuePersonalCoupon();
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<Customer | null>(null);
  const [code, setCode] = useState('');
  const [isSharable, setIsSharable] = useState(false);
  const { data: results } = useCustomerSearch(selected ? '' : term);

  const matches = results?.items ?? [];

  const handleIssue = () => {
    if (!selected) {
      f7.toast.show({ text: tp.pickCustomerError, closeTimeout: 2500, position: 'bottom' });
      return;
    }
    if (code.trim().length === 0) {
      f7.toast.show({ text: tp.enterCouponError, closeTimeout: 2500, position: 'bottom' });
      return;
    }
    issue.mutate(
      { promotionId, customerId: selected.id, code: code.trim(), isSharable },
      {
        onSuccess: () => {
          f7.toast.show({
            text: tp.couponIssuedTo(customerName(selected, tp.customerFallback)),
            closeTimeout: 2500,
            position: 'bottom',
          });
          setSelected(null);
          setTerm('');
          setCode('');
          setIsSharable(false);
        },
        onError: (e) => {
          f7.toast.show({
            text: resolveErrorText(e, errorMessages),
            closeTimeout: 3500,
            position: 'bottom',
          });
        },
      },
    );
  };

  return (
    <>
      <BlockTitle>{tp.issuePersonalCoupon}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {selected ? (
          <ListItem title={customerName(selected, tp.customerFallback)}>
            <F7Link slot="after" onClick={() => setSelected(null)}>
              {tp.change}
            </F7Link>
          </ListItem>
        ) : (
          <ListInput
            label={tp.customer}
            type="text"
            placeholder={tp.customerSearchPlaceholder}
            value={term}
            onInput={(e) => setTerm(e.target.value)}
          />
        )}
        {!selected &&
          matches.map((c) => (
            <ListItem
              key={c.id}
              link="#"
              title={customerName(c, tp.customerFallback)}
              after={c.phoneNumbers.find((p) => p.isPrimary)?.phone ?? ''}
              onClick={() => {
                setSelected(c);
                setTerm('');
              }}
            />
          ))}
        <ListInput
          label={tp.couponCode}
          type="text"
          placeholder={tp.couponCodePlaceholder}
          value={code}
          onInput={(e) => setCode(e.target.value.toUpperCase())}
        />
        <ListItem title={tp.sharable} footer={tp.sharableFooter}>
          <ToggleSwitch slot="after" checked={isSharable} onChange={setIsSharable} />
        </ListItem>
      </List>
      <Block>
        <Button fill large onClick={handleIssue} disabled={issue.isPending}>
          {issue.isPending ? tp.issuing : tp.issueCoupon}
        </Button>
      </Block>
    </>
  );
}
